import { useSelector } from 'react-redux';
import { CompositeLayer } from '@deck.gl/core';
import { CartoLayer } from '@deck.gl/carto';
// import { IconLayer } from '@deck.gl/layers';
import { useCartoLayerProps } from '@carto/react-api';
import tractBroadbandSource from 'data/sources/tractBroadbandSource';
import htmlForFeature from 'utils/htmlForFeature';
import { useSearchParams } from 'react-router-dom';
import { broadbandColor } from 'utils/broadbandScores';
import { LAYER_TYPE, isLayerVisible } from 'utils/layerVisibility';
import { useState, useEffect } from 'react';
// import { setViewState } from '@carto/react-redux';
// import { CollisionFilterExtension } from '@deck.gl/extensions';
import ReactDOMServer from 'react-dom/server';
import Popover from '../common/Popover';

export const TRACT_BROADBAND_LAYER_ID = 'tractBroadbandLayer';
export const TRACT_BROADBAND_LAYER_HOVER_ID = 'tractBroadbandLayerHover';
export const ACTIVE_TRACT_BROADBAND_LAYER_ID = 'activeTractBroadbandLayer';

const ACTIVE_COMPARE_SIMILAR_TRACT_A_COLOR = [99, 196, 89];
const ACTIVE_COMPARE_SIMILAR_TRACT_B_COLOR = [210, 104, 208];

export default function TractBroadbandLayer() {
  const [layer, setLayer] = useState(null);
  const [previousActiveTract, setPreviousActiveTract] = useState(null);
  const [previousActiveCompareTract, setPreviousActiveCompareTract] = useState(null);
  const [previousActivePanel, setPreviousActivePanel] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);
  const [previousSimilarTracts, setPreviousSimilarTracts] = useState(null);
  const [previousHoveredTractTractcode, setPreviousHoveredTractTractcode] =
    useState(null);
  const [previousActiveCompareSimilarTract, setPreviousActiveCompareSimilarTract] =
    useState(null);
  const [previousActive, setPreviousActive] = useState(null);
  const [previousYear, setPreviousYear] = useState(null);
  const [previousBroadbandMin, setPreviousBroadbandMin] = useState(null);
  const [previousBroadbandMax, setPreviousBroadbandMax] = useState(null);
  const { tractBroadbandLayer } = useSelector((state) => state.carto.layers);
  const source = tractBroadbandSource(tractBroadbandLayer?.year || 2019);
  const cartoLayerProps = useCartoLayerProps({ source });
  const viewState = useSelector((state) => state.carto.viewState);
  const [searchParams, setSearchParams] = useSearchParams();
  const [hoveredTractTractcode, setHoveredTractTractcode] = useState(null);
  const [active, setActive] = useState(false);
  // const dispatch = useDispatch();

  useEffect(() => {
    let activeState = searchParams.get('active-state') || null;
    let activeCounty = searchParams.get('active-county') || null;
    let activeTract = searchParams.get('active-tract') || null;

    setActive(activeState || activeCounty || activeTract);
  }, [active, searchParams, setActive]);

  // const zoomTractIntoView = useCallback(
  //   ({ lng, lat }) => {
  //     dispatch(
  //       setViewState({
  //         // zoom: 12,
  //         latitude: lat,
  //         longitude: lng,
  //       })
  //     );
  //   },
  //   [dispatch]
  // );

  useEffect(() => {
    if (!tractBroadbandLayer || !source) {
      return;
    }

    if (isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom) === false) {
      setLayer(null);
      return;
    }

    if (
      layer &&
      previousActiveTract === searchParams.get('active-tract') &&
      previousActiveCompareTract === searchParams.get('active-compare-tract') &&
      previousSimilarTracts === searchParams.get('similar-tracts') &&
      previousActiveCompareSimilarTract ===
        searchParams.get('active-compare-similar-tract') &&
      previousSocioeconomicFactor === searchParams.get('socioeconomic-factor') &&
      previousYear === searchParams.get('year') &&
      previousActive === active &&
      previousHoveredTractTractcode === hoveredTractTractcode &&
      previousActivePanel === searchParams.get('active-panel') &&
      previousBroadbandMin === searchParams.get('broadband-min') &&
      previousBroadbandMax === searchParams.get('broadband-max')
    ) {
      return;
    }

    let mainTractBroadbandLayer = new CartoLayer({
      ...cartoLayerProps,
      id: TRACT_BROADBAND_LAYER_ID,
      getFillColor: (object) => {
        let broadbandScore = object.properties.broadband;
        let similarTracts = searchParams.get('similar-tracts')?.split(',');
        let tractCode = object.properties.tractcode;
        let isActiveCompareSimilarTract =
          searchParams.get('active-compare-similar-tract') === tractCode.toString();
        let isActiveCompareTract =
          searchParams.get('active-compare-tract') === tractCode.toString();

        if (
          searchParams.get('active-compare-tract') === tractCode.toString() ||
          searchParams.get('active-tract') === tractCode.toString() ||
          similarTracts?.includes(tractCode.toString()) === true ||
          isActiveCompareSimilarTract === true ||
          hoveredTractTractcode === tractCode
        ) {
          if (isActiveCompareSimilarTract === true || isActiveCompareTract === true) {
            return ACTIVE_COMPARE_SIMILAR_TRACT_B_COLOR;
          }
          if (
            (searchParams.get('active-panel') === 'compare-similar-tracts' ||
              searchParams.get('active-panel') === 'compare-tracts') &&
            searchParams.get('active-tract') === tractCode.toString()
          ) {
            return ACTIVE_COMPARE_SIMILAR_TRACT_A_COLOR;
          }
          return broadbandColor(broadbandScore);
        }

        return broadbandColor(
          broadbandScore,
          active !== null ||
            object.properties.broadband < searchParams.get('broadband-min') ||
            object.properties.broadband > searchParams.get('broadband-max')
        );
      },
      pointRadiusMinPixels: 2,
      getLineColor: [255, 255, 255],
      lineWidthMinPixels: 1,
      // pickable: isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom),
      pickable: true,
      // opacity: isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom) ? 1 : 0,
      onHover: (info) => {
        if (info?.object) {
          info.object = {
            html: htmlForFeature({ feature: info.object }),
            style: {},
          };
        }
      },
      updateTriggers: {
        pickable: [viewState],
        opacity: [viewState],
        getFillColor: [
          active,
          searchParams.get('active-panel'),
          searchParams.get('active-compare-tract'),
          searchParams.get('active-tract'),
          searchParams.get('similar-tracts'),
          searchParams.get('active-compare-similar-tract'),
          searchParams.get('broadband-min'),
          searchParams.get('broadband-max'),
          hoveredTractTractcode,
        ],
      },
    });

    let tractBroadbandHoverLayer = new CartoLayer({
      ...cartoLayerProps,
      id: TRACT_BROADBAND_LAYER_HOVER_ID,
      getFillColor: [0, 0, 0, 0],
      pointRadiusMinPixels: 2,
      getLineColor: (object) => {
        let similarTracts = searchParams.get('similar-tracts')?.split(',');
        let tractCode = object.properties.tractcode;

        return hoveredTractTractcode === tractCode &&
          searchParams.get('active-compare-tract') !== hoveredTractTractcode.toString() &&
          searchParams.get('active-tract') !== hoveredTractTractcode.toString() &&
          similarTracts?.includes(tractCode) === false
          ? [6, 49, 93]
          : [255, 255, 255, 0];
      },
      // opacity: isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom) ? 1 : 0,
      lineWidthMinPixels: 1.5,
      // pickable: isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom),
      pickable: true,
      updateTriggers: {
        opacity: [viewState],
        pickable: [viewState],
        getLineColor: [
          hoveredTractTractcode,
          searchParams.get('active-tract'),
          searchParams.get('active-compare-tract'),
          searchParams.get('similar-tracts'),
        ],
      },
    });

    let activeTractBroadbandLayer = new CartoLayer({
      ...cartoLayerProps,
      id: ACTIVE_TRACT_BROADBAND_LAYER_ID,
      getFillColor: [0, 0, 0, 0],
      pointRadiusMinPixels: 2,
      getLineColor: (object) => {
        let similarTracts = searchParams.get('similar-tracts')?.split(',');
        let tractCode = object.properties.tractcode?.toString();
        let isActiveCompareSimilarTract =
          searchParams.get('active-compare-similar-tract') === tractCode.toString();

        if (
          searchParams.get('active-tract') === tractCode ||
          searchParams.get('active-compare-tract') === tractCode ||
          similarTracts?.includes(tractCode) === true ||
          isActiveCompareSimilarTract === true ||
          hoveredTractTractcode?.toString() === tractCode
        ) {
          if (isActiveCompareSimilarTract === true) {
            return ACTIVE_COMPARE_SIMILAR_TRACT_B_COLOR;
          }
          if (
            searchParams.get('active-panel') === 'compare-similar-tracts' &&
            searchParams.get('active-tract') === tractCode
          ) {
            return ACTIVE_COMPARE_SIMILAR_TRACT_A_COLOR;
          }
          return [6, 49, 93];
        }

        return [255, 255, 255, 0];
      },
      // opacity: isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom) ? 1 : 0,
      lineWidthMinPixels: 1.5,
      // pickable: isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom),
      pickable: true,
      updateTriggers: {
        pickable: [viewState],
        opacity: [viewState],
        getLineColor: [
          searchParams.get('active-panel'),
          searchParams.get('active-tract'),
          searchParams.get('active-compare-tract'),
          searchParams.get('active-compare-similar-tract'),
          searchParams.get('similar-tracts'),
          hoveredTractTractcode,
        ],
      },
    });

    class TractBroadbandCompositeLayer extends CompositeLayer {
      renderLayers() {
        return [
          mainTractBroadbandLayer,
          tractBroadbandHoverLayer,
          activeTractBroadbandLayer,
        ];
      }
    }

    let tractBroadbandCompositeLayer = new TractBroadbandCompositeLayer({
      onHover: (info) => {
        setHoveredTractTractcode(info.object?.properties?.tractcode || null);

        if (info?.object) {
          info.object = {
            html: ReactDOMServer.renderToStaticMarkup(
              <Popover>
                <div className='whitespace-nowrap flex flex-col gap-1'>
                  <div className='flex gap-12 items-center justify-between'>
                    <div>
                      {info.object?.properties?.tractcode}
                      <span className='text-blue-300'>
                        , {info.object?.properties?.countyname},{' '}
                        {info.object?.properties?.statename}
                      </span>
                    </div>
                    <div className='text-blue-300 text-sm'>
                      ({info.object?.properties?.year})
                    </div>
                  </div>
                  <div className='text-sm'>
                    Broadband:{' '}
                    <span className='text-blue-300'>
                      {Math.round(info.object?.properties?.broadband * 100) / 100}
                    </span>
                  </div>
                </div>
              </Popover>
            ),
            style: {
              background: 'transparent',
            },
          };
        }
      },
      onClick: (info) => {
        setSearchParams((params) => {
          params.set('active-tract', info.object.properties.tractcode);
          params.delete('active-county');
          params.delete('active-state');
          params.delete('active-panel');
          params.delete('active-compare-county');
          params.delete('active-compare-state');
          params.delete('active-compare-tract');
          params.delete('active-compare-similar-tract');

          return params;
        });

        // zoomTractIntoView({
        //   lng: info.object.properties.lng,
        //   lat: info.object.properties.lat,
        // });
      },
      updateTriggers: {
        getLineColor: [
          hoveredTractTractcode,
          searchParams.get('active-panel'),
          searchParams.get('active-tract'),
          searchParams.get('active-compare-tract'),
          searchParams.get('active-compare-similar-tract'),
          searchParams.get('similar-tracts'),
        ],
        lineWidthMinPixels: [hoveredTractTractcode],
        opacity: [viewState],
        pickable: [viewState],
        getFillColor: [
          active,
          searchParams.get('active-panel'),
          searchParams.get('active-tract'),
          searchParams.get('active-compare-tract'),
          searchParams.get('similar-tracts'),
          searchParams.get('active-compare-similar-tract'),
          searchParams.get('broadband-min'),
          searchParams.get('broadband-max'),
          hoveredTractTractcode,
        ],
        getSize: [
          searchParams.get('active-tract'),
          searchParams.get('active-compare-tract'),
          searchParams.get('similar-tracts'),
        ],
      },
    });

    setLayer(tractBroadbandCompositeLayer);
    setPreviousActiveTract(searchParams.get('active-tract'));
    setPreviousActiveCompareTract(searchParams.get('active-compare-tract'));
    setPreviousSocioeconomicFactor(searchParams.get('socioeconomic-factor'));
    setPreviousActive(active);
    setPreviousActivePanel(searchParams.get('active-panel'));
    setPreviousYear(searchParams.get('year'));
    setPreviousHoveredTractTractcode(hoveredTractTractcode);
    setPreviousSimilarTracts(searchParams.get('similar-tracts'));
    setPreviousActiveCompareSimilarTract(
      searchParams.get('active-compare-similar-tract')
    );
    setPreviousBroadbandMin(searchParams.get('broadband-min'));
    setPreviousBroadbandMax(searchParams.get('broadband-max'));
  }, [
    active,
    cartoLayerProps,
    hoveredTractTractcode,
    layer,
    previousActive,
    previousActiveCompareSimilarTract,
    previousActiveCompareTract,
    previousActivePanel,
    previousActiveTract,
    previousBroadbandMax,
    previousBroadbandMin,
    previousHoveredTractTractcode,
    previousSimilarTracts,
    previousSocioeconomicFactor,
    previousYear,
    searchParams,
    setSearchParams,
    source,
    tractBroadbandLayer,
    viewState,
  ]);

  return layer;
}
